import {
  adminPageSchema,
  contentRevisionSchema,
  normalizeRichDoc,
  PAGE_LABELS,
  pageKeySchema,
  richDocText,
  type AdminPage,
  type ContentRevision,
  type PageKey,
  type RichDoc,
} from '@growup/site-shared';
import { Button, Card, Field, Input, Notice } from '@growup/ui';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { RichEditor } from './RichEditor';

const KEYS = pageKeySchema.options;

/** Paginas editaveis: privacidade e termos, com versoes anteriores. */
export function PagesView() {
  const [key, setKey] = useState<PageKey>('privacidade');
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        {KEYS.map((k) => (
          <Button
            key={k}
            size="sm"
            variant={k === key ? 'primary' : 'ghost'}
            onClick={() => setKey(k)}
          >
            {PAGE_LABELS[k].title}
          </Button>
        ))}
      </div>
      <PageEditor key={key} pageKey={key} />
    </div>
  );
}

function PageEditor({ pageKey }: { pageKey: PageKey }) {
  const [page, setPage] = useState<AdminPage | null>(null);
  // Muda quando se repoe uma versao: o editor volta a montar com esse texto.
  const [version, setVersion] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState<RichDoc | null>(null);
  const [history, setHistory] = useState<ContentRevision[] | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getJson(`/api/site/content/pages/${pageKey}`, adminPageSchema)
      .then((p) => {
        setPage(p);
        setTitle(p.title);
        setBody(p.body);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [pageKey]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(null);
    setFields({});
    try {
      const result = adminPageSchema.parse(
        await send('PUT', `/api/site/content/pages/${pageKey}`, { title, body }),
      );
      setPage(result);
      setSaved('Guardado: já está no site.');
      setHistory(null);
    } catch (e) {
      setError(messageOf(e));
      if (e instanceof ApiError) setFields(e.fields);
    } finally {
      setBusy(false);
    }
  };

  const showHistory = async () => {
    try {
      setHistory(
        z
          .array(contentRevisionSchema)
          .parse(await getJson(`/api/site/content/pages/${pageKey}/revisions`, z.unknown())),
      );
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const restore = (r: ContentRevision) => {
    const old = z.object({ title: z.string(), body: z.unknown() }).safeParse(r.content);
    if (!old.success) return;
    setTitle(old.data.title);
    setBody(normalizeRichDoc(old.data.body));
    setVersion((v) => v + 1);
    setSaved('Versão anterior carregada no editor. Guarde para a pôr no site.');
  };

  if (!page || !body) return error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>;

  return (
    <div className="grid items-start gap-4 xl:grid-cols-[1fr_22rem]">
      <Card headingLevel={2} title={PAGE_LABELS[pageKey].title}>
        <div className="flex flex-col gap-4">
          <Field label="Título" error={fields.title}>
            <Input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <RichEditor
            key={version}
            label="Texto"
            value={body}
            onChange={setBody}
            error={fields.body ?? fields['']}
          />
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          <div>
            <Button disabled={busy} onClick={() => void save()}>
              Guardar e publicar
            </Button>
          </div>
        </div>
      </Card>
      <Card headingLevel={2} title="Versões">
        <div className="flex flex-col gap-3">
          <p className="text-caption text-gu-ink-muted">
            No site em growup.gg{PAGE_LABELS[pageKey].path}. Última alteração:{' '}
            {formatDateTime(page.updatedAt)} ({page.updatedBy}).
          </p>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void (history ? setHistory(null) : showHistory())}
          >
            {history ? 'Fechar histórico' : 'Ver versões anteriores'}
          </Button>
          {history?.length === 0 ? (
            <p className="text-caption text-gu-ink-muted">Ainda não há versões anteriores.</p>
          ) : null}
          <ol className="flex flex-col gap-3">
            {history?.map((r) => {
              const old = z.object({ title: z.string(), body: z.unknown() }).safeParse(r.content);
              const words = old.success
                ? richDocText(normalizeRichDoc(old.data.body)).split(/\s+/).filter(Boolean).length
                : 0;
              return (
                <li key={r.id} className="flex flex-col gap-1 border-t border-gu-line pt-2">
                  <span className="font-semibold">
                    {r.createdBy === 'texto inicial'
                      ? 'Texto inicial'
                      : formatDateTime(r.createdAt)}
                  </span>
                  <span className="text-caption text-gu-ink-muted">
                    {r.createdBy === 'texto inicial' ? 'Rascunho de origem' : r.createdBy} · {words}{' '}
                    palavras
                  </span>
                  <span>
                    <Button size="sm" variant="secondary" onClick={() => restore(r)}>
                      Repor esta versão
                    </Button>
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </Card>
    </div>
  );
}
